import type { FunctionComponent } from 'react';
import { useEffect, useMemo } from 'react';

import { useTranslate } from '~/shared/context/use-translate';

import { useActiveThreadStore } from './ActiveThreadStoreProvider';
import { useAllLanguageCommentsQuery } from './api/use-comments-query';
import { type CommentDocumentState, classifyComments } from './comment-classification';
import { useCommentsScope } from './comments-scope-context';

type CommentsClassificationPublisherProps = {
    readonly documentState: CommentDocumentState;
};

export const CommentsClassificationPublisher: FunctionComponent<CommentsClassificationPublisherProps> = ({
    documentState,
}) => {
    const scope = useCommentsScope();
    const translate = useTranslate();
    const setCommentClassification = useActiveThreadStore((state) => state.setCommentClassification);
    const { data: comments } = useAllLanguageCommentsQuery();
    const labels = useMemo(
        () => ({
            title: translate('pages.job-architecture-v2.comments.hidden.groups.title'),
            sectionFallback: translate('pages.job-architecture-v2.comments.hidden.groups.section-fallback'),
            skillFallback: translate('pages.job-architecture-v2.comments.hidden.groups.skill-fallback'),
        }),
        [translate],
    );
    const classification = useMemo(
        () =>
            classifyComments({
                comments: comments ?? [],
                documentState,
                labels,
                language: scope.language,
            }),
        [comments, documentState, labels, scope.language],
    );

    useEffect(() => {
        setCommentClassification(classification, scope);
    }, [classification, scope, setCommentClassification]);

    useEffect(() => {
        return () => setCommentClassification(null);
    }, [setCommentClassification]);

    return null;
};
